import React, { useMemo } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './BookingOverviewCalendar.css';
import { formatLocalYYYYMMDD, parseDateOnlyLocal } from '../utils/dateUtils';
import { OverviewDaySets, allOverviewDates } from '../utils/overviewCalendar';

export interface BookingOverviewCalendarProps {
  daySets: OverviewDaySets;
  compact?: boolean;
  className?: string;
}

function dayOverviewClasses(
  date: Date,
  sets: OverviewDaySets
): string {
  const key = formatLocalYYYYMMDD(date);
  const parts: string[] = [];
  if (sets.manual.has(key)) parts.push('overview-day--manual');
  if (sets.pending.has(key)) parts.push('overview-day--pending');
  if (sets.confirmed.has(key)) parts.push('overview-day--confirmed');
  return parts.join(' ');
}

const BookingOverviewCalendar: React.FC<BookingOverviewCalendarProps> = ({
  daySets,
  compact = false,
  className = '',
}) => {
  const dates = useMemo(() => allOverviewDates(daySets), [daySets]);

  const highlightDates = useMemo(
    () => dates.map(parseDateOnlyLocal).filter((d) => !Number.isNaN(d.getTime())),
    [dates]
  );

  const openToDate = useMemo(() => {
    if (highlightDates.length === 0) return new Date();
    return highlightDates.reduce((earliest, d) => (d < earliest ? d : earliest));
  }, [highlightDates]);

  const counts = useMemo(
    () => ({
      manual: daySets.manual.size,
      pending: daySets.pending.size,
      confirmed: daySets.confirmed.size,
    }),
    [daySets]
  );

  if (dates.length === 0) {
    return (
      <p className="overview-calendar-empty">
        No manual blocks, pending applications, or confirmed stays in this view.
      </p>
    );
  }

  return (
    <div
      className={`booking-overview-calendar${compact ? ' booking-overview-calendar--compact' : ''}${className ? ` ${className}` : ''}`}
    >
      <DatePicker
        key={`${openToDate.toISOString()}-${dates.length}`}
        inline
        selected={null}
        openToDate={openToDate}
        onChange={() => undefined}
        highlightDates={highlightDates}
        dayClassName={(date) => dayOverviewClasses(date, daySets)}
        monthsShown={compact ? 1 : 2}
        disabledKeyboardNavigation
        calendarClassName="booking-overview-calendar-picker"
      />
      <ul className="overview-calendar-legend" aria-label="Calendar legend">
        <li>
          <span className="overview-legend-swatch overview-legend-swatch--manual" />
          Manual block ({counts.manual} {counts.manual === 1 ? 'day' : 'days'})
        </li>
        <li>
          <span className="overview-legend-swatch overview-legend-swatch--pending" />
          Pending application ({counts.pending} {counts.pending === 1 ? 'day' : 'days'})
        </li>
        <li>
          <span className="overview-legend-swatch overview-legend-swatch--confirmed" />
          Confirmed booking ({counts.confirmed} {counts.confirmed === 1 ? 'day' : 'days'})
        </li>
      </ul>
      {!compact && (
        <p className="overview-calendar-note">Days with more than one type use a split colour.</p>
      )}
    </div>
  );
};

export default BookingOverviewCalendar;
